import { useEffect, useRef, useState } from 'react'

/**
 * Counts up from 0 to `target` once the element scrolls into view.
 * Mirrors the ease-out-cubic animateCount() logic from script.js.
 */
export function useCountUp(target, suffix = '', duration = 1400) {
    const [value, setValue] = useState(0)
    const ref = useRef(null)
    const started = useRef(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        function animate() {
            let start = null
            function step(timestamp) {
                if (!start) start = timestamp
                const progress = Math.min((timestamp - start) / duration, 1)
                const eased = 1 - Math.pow(1 - progress, 3)
                setValue(Math.floor(eased * target))
                if (progress < 1) {
                    window.requestAnimationFrame(step)
                } else {
                    setValue(target)
                }
            }
            window.requestAnimationFrame(step)
        }

        if (!('IntersectionObserver' in window)) {
            animate()
            return
        }

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !started.current) {
                    started.current = true
                    animate()
                    obs.unobserve(entry.target)
                }
            })
        }, { threshold: 0.5 })

        observer.observe(el)
        return () => observer.disconnect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, duration])

    return { ref, display: `${value.toLocaleString()}${suffix}` }
}
